import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const youtubeEmbedUrl = (url) => {
  const patterns = [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /youtu\.be\/([a-zA-Z0-9_-]+)/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
  }

  return null;
};

const renderDescriptionHtml = (html) => {
  const convertedHtml = html.replace(
    /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)[^\s<]*)/g,
    (url) => {
      const embedUrl = youtubeEmbedUrl(url);
      if (!embedUrl) return url;

      return `
        <div class="ratio ratio-16x9 my-4">
          <iframe
            src="${embedUrl}"
            width="600"
            title="YouTube video"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen>
          </iframe>
        </div>
      `;
    },
  );

  return { __html: convertedHtml };
};

const trackEvent = (eventName, params = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
};

function GameShow({ gameId }) {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showGameControlsModal, setShowGameControlsModal] = useState(false);

  useEffect(() => {
    if (game?.name) {
      document.title = `${game.name} | CS2N Games`;
    }

    return () => {
      document.title = "CS2N Games";
    };
  }, [game?.name]);

  useEffect(() => {
    fetch(`/api/games/${gameId}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        setGame(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading game:", error);
        setLoading(false);
      });
  }, [gameId]);

  useEffect(() => {
    if (!showGameModal && !showGameControlsModal) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowGameModal(false);
        setShowGameControlsModal(false);
      }
    };

    document.body.classList.add("modal-open");
    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", handleEsc);
    };
  }, [showGameModal, showGameControlsModal]);

  useEffect(() => {
    if (!game) return;

    trackEvent("game_view", {
      game_id: game.id,
      game_name: game.name,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  }, [game]);

  const handleOpenGameInNewTab = () => {
    trackEvent("game_open_new_tab", {
      game_id: game.id,
      game_name: game.name,
      game_url: game.url,
      page_path: window.location.pathname,
    });

    setShowGameModal(false);
  };

  const handleOpenGameControls = () => {
    trackEvent("game_controls_open", {
      game_id: game.id,
      game_name: game.name,
      page_path: window.location.pathname,
    });

    setShowGameControlsModal(true);
  };

  const handlePlayGame = () => {
    trackEvent("game_play_click", {
      game_id: game.id,
      game_name: game.name,
      game_url: game.url,
      page_path: window.location.pathname,
    });

    setShowGameModal(true);
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <main className="container mt-5">
          <p>Loading game...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!game) {
    return (
      <div>
        <Navbar />
        <main className="container mt-5">
          <p>Game not found.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column">
      <Navbar />

      <main className="container mt-5 mb-5 flex-grow-1">
        <a href="/" className="btn btn-link px-0 mb-3">
          ← Back to games
        </a>

        {game.header_image_url && (
          <div className="mb-4">
            <img
              src={game.header_image_url}
              className="img-fluid rounded shadow"
              style={{
                maxHeight: "200px",
                width: "100%",
                objectFit: "cover",
              }}
              alt={game.name}
            />
          </div>
        )}

        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <h1 className="card-title mb-1">{game.name}</h1>
                  </div>

                  <div className="d-flex flex-column flex-sm-row gap-2">
                    {game.game_controls && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-lg"
                        onClick={() => setShowGameControlsModal(true)}
                      >
                        Game Controls
                      </button>
                    )}

                    {game.url && (
                      <button
                        type="button"
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowGameModal(true)}
                      >
                        Play Game
                      </button>
                    )}
                  </div>
                </div>

                {game.description && (
                  <div
                    className="card-text trix-content"
                    dangerouslySetInnerHTML={renderDescriptionHtml(
                      game.description,
                    )}
                  />
                )}
              </div>
            </div>
          </div>

          <aside className="col-lg-4">
            <div className="card mb-4">
              <div className="card-body">
                <h2 className="h5 card-title">Content Rating</h2>

                {game.content_rating ? (
                  <div
                    className="trix-content"
                    dangerouslySetInnerHTML={renderDescriptionHtml(
                      game.content_rating,
                    )}
                  />
                ) : (
                  <p className="text-muted mb-0">
                    No content rating available.
                  </p>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h2 className="h5 card-title">Screenshots</h2>
                <p className="text-muted mb-0">Screenshots coming soon.</p>
              </div>
            </div>

            {game.acknowledgement && (
              <div className="card mt-4">
                <div className="card-body">
                  <h2 className="h5 card-title">Acknowledgement</h2>
                  <div
                    className="trix-content small"
                    dangerouslySetInnerHTML={{ __html: game.acknowledgement }}
                  />
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>

      {showGameModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-fullscreen m-0">
              <div className="modal-content border-0">
                <div className="modal-header bg-dark text-white border-0">
                  <h5 className="modal-title">{game.name}</h5>

                  <div className="d-flex gap-3 align-items-center ms-auto">
                    <a
                      href={game.url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-light btn-sm px-3"
                      onClick={() => setShowGameModal(false)}
                    >
                      Open in new tab
                    </a>

                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      aria-label="Close"
                      onClick={() => setShowGameModal(false)}
                    />
                  </div>
                </div>

                <div className="modal-body p-0 bg-dark">
                  <iframe
                    key={showGameModal ? "game-open" : "game-closed"}
                    src={showGameModal ? game.url : ""}
                    title={game.name}
                    style={{
                      width: "100%",
                      height: "calc(100vh - 57px)",
                      border: "0",
                      backgroundColor: "#000",
                    }}
                    allow="fullscreen; autoplay; gamepad"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
            onClick={() => setShowGameModal(false)}
          />
        </>
      )}

      {showGameControlsModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Game Controls</h5>

                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowGameControlsModal(false)}
                  />
                </div>

                <div className="modal-body">
                  {game.game_controls ? (
                    <div
                      className="trix-content"
                      dangerouslySetInnerHTML={renderDescriptionHtml(
                        game.game_controls,
                      )}
                    />
                  ) : (
                    <p className="text-muted mb-0">
                      No game controls have been added yet.
                    </p>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowGameControlsModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            className="modal-backdrop fade show"
            onClick={() => setShowGameControlsModal(false)}
          />
        </>
      )}

      <Footer />
    </div>
  );
}

export default GameShow;
